import { ContentProps } from "@/data/types/ContentProps";
import { getSettings, useSettings } from "@/data/Settings";
import { dataMap as helloRetailProductDataMap } from "@/data/utils/helloRetailSearch";
import useSWR, { unstable_serialize } from "swr";
import { shrink } from "@/data/utils/keyUtil";
import { getPageDataFromId } from "@/data/_PageDataFromId-Server";
import { getCategories, useCategories } from "@/data/Content/Categories";
import { getCookie } from "@/utils/clickAndCollect";
import { usePageDataFromId } from "@/data/_PageDataFromId";
import { ID } from "@/data/types/Basic";
import { HELLO_RETAIL_TRACKING_ID } from "@/data/constants/HelloretailSearch";
import { HelloRetailRecomRequest } from "@/data/types/HelloRetail";
import { WidgetProperties } from "@/data/types/Slot";

type RequestContext = {
  extraData?: Record<string, unknown>;
  [key: string]: unknown;
};

const DATA_KEY = "HELLO_RETAIL_PRODUCT_RECOMMENDATIONS";

const fetcher = async (url: string, options: any) => {
    try {
        const response = await fetch(url, options);
        const data = await response.json();
        return data?.responses?.at(0)?.products ?? [];
    } catch (error) {
        return [];
    }
}

export const getHelloRetailProductRecommendations = async ({ cache, id, context, properties }: ContentProps) => {
    const settings = await getSettings(cache, context);
    const helloRetailUUID = settings.userData.HelloRetailUUID;
    const recommendationBoxId = properties?.recommendationBoxId as string;
    const helloRetailRecommendationType = (properties?.helloRetailRecommendationType ?? "other_page") as string;
    const helloRetailRecommendationURL = settings.userData.HelloRetailRecommendationURL;
    const recommendationBoxFilter = properties?.recommendationBoxFilter as string;
    const recommendationBoxFilterValue = properties?.recommendationBoxFilterValue as string;
    const trackingUserId = context.req.cookies?.hello_retail_id ?? "";
    const pageData = await getPageDataFromId(cache, context.query.path, context);

    let partnumbersInCart: string[] = [];

    try {
        const partnumbersInCartStr = context.req.cookies?.PARTNUMBERS_IN_CART;
        partnumbersInCart = partnumbersInCartStr ? JSON.parse(partnumbersInCartStr) : [];
    } catch (e) {
        console.error("Failed to parse PARTNUMBERS_IN_CART cookie", e);
    }

    const requestContext : RequestContext = {
        productNumbers: partnumbersInCart
    }

    var payload: HelloRetailRecomRequest = {
        websiteUuid: helloRetailUUID,
        trackingUserId,
        requests: [
            {
                key: recommendationBoxId,
                context : requestContext
            }
        ]
    };

    if (helloRetailRecommendationType === "product_page") {
        const currentProduct = pageData?.tokenExternalValue;
        if(currentProduct && !partnumbersInCart.includes(currentProduct)) {
            requestContext.productNumbers = [...partnumbersInCart, currentProduct];
        }
    }

    if(helloRetailRecommendationType === "category_page"){
        if(recommendationBoxFilter){
            if (recommendationBoxFilter?.includes("extraData")) {
                const key = recommendationBoxFilter.replace("extraData.", "");
                if (key) {
                    requestContext.extraData = {...(requestContext.extraData || {}), [key]: recommendationBoxFilterValue};
                }
            } else {
                requestContext[recommendationBoxFilter] = recommendationBoxFilterValue;
            }
        } else {
            const categories = await getCategories({ cache, id, context });
            const categoryName = categories?.catalogGroupView?.at(0)?.name ?? "";
            requestContext.hierarchies = categoryName ? [[categoryName]] : [];
        }
    }

    var props = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
    }
    const cacheScope = {requestScope:true};
    const key = unstable_serialize([shrink(props), DATA_KEY]);
    const value = cache.get(key, cacheScope) || helloRetailProductDataMap(await fetcher(helloRetailRecommendationURL, props));

    cache.set(key, value, cacheScope);
    cache.set(HELLO_RETAIL_TRACKING_ID, trackingUserId, cacheScope);
}

export const useHelloRetailProductRecommendations = (properties?: WidgetProperties, id?:ID) => {
    const { settings } = useSettings();
    const helloRetailUUID = settings.userData.HelloRetailUUID;
    const helloRetailRecommendationURL = settings.userData.HelloRetailRecommendationURL;
    const recommendationBoxId = properties?.recommendationBoxId as string;
    const helloRetailRecommendationType = (properties?.helloRetailRecommendationType ?? "other_page") as string;
    const recommendationBoxFilter = properties?.recommendationBoxFilter as string;
    const recommendationBoxFilterValue = properties?.recommendationBoxFilterValue as string;

    let trackingUserId = getCookie(HELLO_RETAIL_TRACKING_ID) ?? "";
    const {data : fallbackTrackingUserId} = useSWR(!trackingUserId && recommendationBoxId ? HELLO_RETAIL_TRACKING_ID : null);
    if(!trackingUserId && fallbackTrackingUserId){
        trackingUserId = fallbackTrackingUserId;
    }

    const { data: pageData} = usePageDataFromId();
    const {catGrpDetails} = useCategories(!recommendationBoxFilter && id?.toString()?[id?.toString()]:[]);;

    let partnumbersInCart: string[] = [];
    try {
        const partnumbersInCartStr = getCookie("PARTNUMBERS_IN_CART") ?? "";
        partnumbersInCart = partnumbersInCartStr ? JSON.parse(partnumbersInCartStr) : [];
    } catch (e) {
        console.error("Failed to parse PARTNUMBERS_IN_CART cookie", e);
    }

    const requestContext : RequestContext = {
        productNumbers: partnumbersInCart
    }

    const payload:HelloRetailRecomRequest = {
        websiteUuid: helloRetailUUID,
        trackingUserId,
        requests: [
            {
                key: recommendationBoxId,
                context: requestContext
            }
        ]
    };

    if(helloRetailRecommendationType === "product_page"){
        const currentProduct = pageData?.tokenExternalValue;
        if(currentProduct && !partnumbersInCart.includes(currentProduct)) {
            requestContext.productNumbers = [...partnumbersInCart, currentProduct];
        }
    }

    if (helloRetailRecommendationType === "category_page") {
        if (recommendationBoxFilter) {
            if (recommendationBoxFilter?.includes("extraData")) {
                const key = recommendationBoxFilter.replace("extraData.", "");
                if (key) {
                    requestContext.extraData = {...(requestContext.extraData || {}), [key]: recommendationBoxFilterValue};
                }
            } else {
                requestContext[recommendationBoxFilter] = recommendationBoxFilterValue;
            }
        } else {
            const categoryName = catGrpDetails?.at(0)?.name ?? "";
            requestContext.hierarchies = categoryName ? [[categoryName]] : []
        }
    }

    const props = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
    }

    const {
        data
    } = useSWR(
        recommendationBoxId ?
            [
                shrink(props), DATA_KEY
            ] : null,
        async () => helloRetailProductDataMap(await fetcher(helloRetailRecommendationURL, props)),
        {
            revalidateOnMount: true
        }
    );

    const helloRetailClickTracking = (trackingCode: string) => {
        if (typeof window !== "undefined" && window.hrq?.push) {
            const hrq = window.hrq || [];
            hrq.push([
                "trackClick",
                trackingCode
            ]);
        }

    };
    return {
        products: data ?? [],
        helloRetailClickTracking
    };
}
